import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import { commitMutation, fetchQuery, graphql, useRelayEnvironment } from 'react-relay';
import type { Environment } from 'relay-runtime';
import type { SessionProviderQuery } from '@/__generated__/SessionProviderQuery.graphql';
import type { SessionProviderSignInMutation } from '@/__generated__/SessionProviderSignInMutation.graphql';
import type { SessionProviderSignUpMutation } from '@/__generated__/SessionProviderSignUpMutation.graphql';
import type { SessionProviderSignOutMutation } from '@/__generated__/SessionProviderSignOutMutation.graphql';
import { NETWORK_ERROR, SessionContext, type AuthResult, type Session, type SessionState } from './context';

export * from './context';

const sessionQuery = graphql`
  query SessionProviderQuery {
    viewer {
      id
      name
      email
      canAccessFinancas
    }
    authStatus {
      enabled
      signupMode
    }
  }
`;

const signInMutation = graphql`
  mutation SessionProviderSignInMutation($input: SignInInput!) {
    signIn(input: $input) {
      error
      user {
        id
      }
    }
  }
`;

const signUpMutation = graphql`
  mutation SessionProviderSignUpMutation($input: SignUpInput!) {
    signUp(input: $input) {
      error
      user {
        id
      }
    }
  }
`;

const signOutMutation = graphql`
  mutation SessionProviderSignOutMutation {
    signOut(input: {}) {
      ok
    }
  }
`;

const mutate = <T extends { response: unknown; variables: object }>(environment: Environment, mutation: Parameters<typeof commitMutation>[1]['mutation'], variables: T['variables']) =>
  new Promise<T['response']>((resolve, reject) => {
    commitMutation(environment, { mutation, variables, onCompleted: (r) => resolve(r as T['response']), onError: reject });
  });

export function SessionProvider({ children }: { children: ReactNode }) {
  const environment = useRelayEnvironment();
  const [state, setState] = useState<SessionState>({ status: 'loading' });

  const refresh = useCallback(async () => {
    try {
      const data = await fetchQuery<SessionProviderQuery>(environment, sessionQuery, {}, { fetchPolicy: 'network-only' }).toPromise();
      if (!data?.authStatus?.enabled) setState({ status: 'demo' });
      else setState({ status: 'ready', viewer: data.viewer ?? null, signupMode: data.authStatus.signupMode });
    } catch {
      setState({ status: 'demo' });
    }
  }, [environment]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const value = useMemo<Session>(() => {
    const run = async (fn: () => Promise<{ error?: string | null } | null | undefined>): Promise<AuthResult> => {
      try {
        const r = await fn();
        if (r?.error) return { error: r.error };
        await refresh();
        return { error: null };
      } catch {
        return { error: NETWORK_ERROR };
      }
    };
    return {
      ...state,
      refresh,
      signIn: (email, password) =>
        run(async () => (await mutate<SessionProviderSignInMutation>(environment, signInMutation, { input: { email, password } })).signIn),
      signUp: (name, email, password) =>
        run(async () => (await mutate<SessionProviderSignUpMutation>(environment, signUpMutation, { input: { name, email, password } })).signUp),
      signOut: async () => {
        try {
          await mutate<SessionProviderSignOutMutation>(environment, signOutMutation, {});
        } finally {
          await refresh();
        }
      },
    };
  }, [state, refresh, environment]);

  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}
